import { MutationTree } from "vuex";
import { MutationTypes } from "./mutation-types";
import { defaultsBookmarks, State } from "./state";
import {
  Bookmark,
  CheckItem,
  Column,
  Comment,
  Label,
  Notification,
  TaskData,
  TaskForm,
  UserInfo,
} from "@/types/task";
import { getTodayDate, getUID, handleRootThemeMode } from "@/utils/share";
import Storage from "@/store/share/storage";
import { ID } from "@/types/task";
import { getters } from "@/store/share/getters";
import { File } from "@/types/drive";
import { DEFAULT_COLUMN, DEFAULT_LABELS } from "@/types/constant";

type AddTaskType = {
  task: TaskForm;
  columnId: ID;
};
type EditTaskType = {
  task: TaskData;
  columnId: ID;
};
type updateColumnType = {
  tasks: Array<TaskData>;
  columnId: ID;
};
type addCommentType = {
  columnId: ID;
  taskId: ID;
  content: string;
};
type editCommentType = {
  columnId: ID;
  taskId: ID;
  content: string;
  commentId: ID;
};
type deleteCommentType = {
  columnId: ID;
  taskId: ID;
  commentId: ID;
};
type loginDataType = {
  userInfo: UserInfo;
  token: string;
};
type ArchiveTaskType = {
  columnId: ID;
  taskId: ID;
  status: boolean;
};
type bookmarkDataType = {
  index: number;
  bookmark: Bookmark;
};
export type LoadingDataType = {
  text?: string;
  isOpen?: boolean;
};

export type Mutations<S = State> = {
  [MutationTypes.SET_LOGIN_DIALOG_STATUS](state: S, isOpen: boolean): void;
  [MutationTypes.SET_STATE](state: S, data: State): void;
  [MutationTypes.SET_DRIVE_FILE](state: S, file: File): void;
  [MutationTypes.SET_ADD_TASK_DIALOG_NAME](state: S, name: string | null): void;
  [MutationTypes.SET_IS_ADD_TASK_DIALOG_STATUS](
    state: S,
    isOpen: boolean
  ): void;
  [MutationTypes.SET_CHECKLIST_STATUS](state: S, isOpen: boolean): void;
  [MutationTypes.SET_MOBILE_SIDEBAR](state: S, isOpen: boolean): void;
  [MutationTypes.UPDATE_CHECKLIST](state: S, item: CheckItem): void;
  [MutationTypes.DELETE_CHECKLIST](state: S, id: ID): void;
  [MutationTypes.SET_GOOGLE_TOKEN](state: S, token: string): void;
  [MutationTypes.SET_USER_INFO](state: S, data: UserInfo): void;
  [MutationTypes.SET_LOGIN_DATA](state: S, data: loginDataType): void;
  [MutationTypes.SET_BOOKMARK](state: S, data: bookmarkDataType): void;
  [MutationTypes.ADD_LABEL](state: S, label: Label): void;
  [MutationTypes.DELETE_BOOKMARK](state: S, index: number): void;
  [MutationTypes.SET_COLUMNS](state: S, columns: Array<Column>): void;
  [MutationTypes.UPDATE_COLUMN](state: S, data: updateColumnType): void;
  [MutationTypes.ADD_TASK](state: S, data: AddTaskType): void;
  [MutationTypes.EDIT_TASK](state: S, data: EditTaskType): void;
  [MutationTypes.ARCHIVE_TASK](state: S, data: ArchiveTaskType): void;
  [MutationTypes.SET_ARCHIVE_STATUS](state: S, status: boolean): void;
  [MutationTypes.ADD_COMMENT](state: S, data: addCommentType): void;
  [MutationTypes.EDIT_COMMENT](state: S, data: editCommentType): void;
  [MutationTypes.DELETE_COMMENT](state: S, data: deleteCommentType): void;
  [MutationTypes.SEARCH_COLUMNS](state: S, text: string): void;
  [MutationTypes.DARK_MODE](state: S): void;
  [MutationTypes.LIGHT_MODE](state: S): void;
  [MutationTypes.SET_BACKGROUND_COLOR](state: S, color: string): void;
  [MutationTypes.SET_BACKGROUND_IMAGE](state: S, image: Blob): void;
  [MutationTypes.SET_NOTIFICATION](state: S, notification: Notification): void;
  [MutationTypes.DELETE_NOTIFICATION](state: S, name: string): void;
  [MutationTypes.SET_LOADING](state: S, loading: LoadingDataType): void;
};

export const mutations: MutationTree<State> & Mutations = {
  [MutationTypes.SET_STATE](state, data): void {
    state.columns = data.columns;
    state.checkList = data.checkList;
    state.bookmarks = data.bookmarks;
    state.labels = data.labels;
    state.settings = data.settings;
    handleRootThemeMode(data.settings?.isDarkMode ? "dark" : "light");
  },

  [MutationTypes.SET_LOGIN_DIALOG_STATUS](state, isOpen: boolean): void {
    state.isOpenLoginDialog = isOpen;
  },

  [MutationTypes.SET_CALENDAR_DIALOG_STATUS](state, isOpen: boolean): void {
    state.isOpenCalendar = isOpen;
  },

  [MutationTypes.SET_ADD_TASK_DIALOG_NAME](state, name: string | null): void {
    state.addTaskDialogName = name;
  },

  [MutationTypes.SET_IS_ADD_TASK_DIALOG_STATUS](state, isOpen: boolean): void {
    state.isAddTaskOpen = isOpen;
  },

  [MutationTypes.SET_GOOGLE_TOKEN](state, token: string): void {
    state.googleToken = token;

    storage.set();
  },

  [MutationTypes.SET_DRIVE_FILE](state, file): void {
    state.googleFile = file;

    storage.setOnLocalStorage();
  },

  [MutationTypes.SET_USER_INFO](state, data: UserInfo): void {
    state.userInfo = data;

    storage.set();
  },

  [MutationTypes.SET_LOGIN_DATA](state, { token, userInfo }): void {
    state.googleToken = token;
    state.userInfo = userInfo;

    // storage.set();
  },

  [MutationTypes.LOGOUT](state): void {
    state.googleToken = "";
    state.userInfo = null;
    state.checkList = [];
    state.bookmarks = defaultsBookmarks();
    state.googleFile = null;
    state.columns = [
      {
        id: 1,
        title: "برای انجام",
        name: "todo",
        tasks: [],
      },
      {
        id: 2,
        title: "درحال انجام",
        name: "doing",
        tasks: [],
      },
      {
        id: 3,
        title: "انجام شده",
        name: "done",
        tasks: [],
      },
    ];
    state.labels = DEFAULT_LABELS;

    storage.setOnLocalStorage();
  },

  [MutationTypes.DARK_MODE](state): void {
    state.settings.isDarkMode = true;
    handleRootThemeMode("dark");

    storage.set({ isOpen: false });
  },

  [MutationTypes.LIGHT_MODE](state): void {
    state.settings.isDarkMode = false;
    storage.set({ isOpen: false });
  },

  [MutationTypes.SET_BACKGROUND_COLOR](state, color): void {
    state.settings.backgroundColor = color;
    state.settings.backgroundImage = null;

    storage.set({ isOpen: false });
  },

  [MutationTypes.SET_BACKGROUND_IMAGE](state, image): void {
    const reader = new FileReader();
    let baseString;
    reader.onloadend = function () {
      baseString = reader.result;
      state.settings.backgroundImage = baseString;
      state.settings.backgroundColor = null;
      storage.set();
    };
    reader.readAsDataURL(image);
  },

  [MutationTypes.SET_ARCHIVE_STATUS](state, status): void {
    state.isArchive = status;
  },

  [MutationTypes.SET_NOTIFICATION](state, notification): void {
    state.notifications.push(notification);

    if (notification.time) {
      setTimeout(() => {
        state.notifications = state.notifications.filter(
          (notificationMap: Notification) =>
            notificationMap.name !== notification.name
        );
      }, notification.time);
    }
  },

  [MutationTypes.DELETE_NOTIFICATION](state, name): void {
    state.notifications = state.notifications.filter(
      (notification: Notification) => notification.name !== name
    );
  },

  [MutationTypes.SET_LOADING](state, loading): void {
    state.loading.isOpen = loading.isOpen;
    state.loading.text = loading?.text || state.loading.text;
  },

  [MutationTypes.SET_BOOKMARK](state, { bookmark, index }): void {
    state.bookmarks[index] = bookmark;

    const allBookemarked = state.bookmarks.every(
      (bookmark: Bookmark | null) => bookmark !== null
    );
    if (allBookemarked) {
      state.bookmarks.push(null);
    }

    storage.set();
  },

  [MutationTypes.DELETE_BOOKMARK](state, index): void {
    state.bookmarks[index] = null;

    storage.set();
  },

  [MutationTypes.ADD_LABEL](state, label): void {
    state.labels.push(label);

    storage.set();
  },

  /*
   * ==================== CHECKLIST ====================
   */

  [MutationTypes.SET_CHECKLIST_STATUS](state, isOpen: boolean): void {
    state.settings.isCheckListOpen = isOpen;

    // Save on storage
    storage.set({ isOpen: false });
  },

  [MutationTypes.SET_MOBILE_SIDEBAR](state, isOpen: boolean): void {
    state.settings.isOpenMobileSidebar = isOpen;
  },

  [MutationTypes.SET_CHECKLIST](state, checkList: Array<CheckItem>): void {
    state.checkList = checkList;

    // UPDATE FROM STORAGE
    storage.set();
  },

  [MutationTypes.UPDATE_CHECKLIST](state, checkItem): void {
    // Find check item index
    const checkItemIndex = state.checkList.findIndex(
      (item) => item?.id === checkItem?.id
    );
    // Update from store state
    state.checkList[checkItemIndex] = checkItem;

    // UPDATE FROM STORAGE
    storage.set();
  },

  [MutationTypes.ADD_CHECKITEM](state, description: string): void {
    const uid = getUID("check");
    const checkItem: CheckItem = {
      id: uid,
      description,
      isChecked: false,
    };

    state.checkList.push(checkItem);

    // Save on storage
    storage.set();
  },

  [MutationTypes.DELETE_CHECKLIST](state, itemId: ID): void {
    state.checkList = state.checkList.filter((item) => item?.id !== itemId);

    // Save on storage
    storage.set();
  },

  /*
   * ==================== COLUMNS ====================
   */

  [MutationTypes.SET_COLUMNS](state, columns: Array<Column>): void {
    state.columns = columns;

    // UPDATE FROM STORAGE
    storage.set();
  },

  [MutationTypes.UPDATE_COLUMN](state, { tasks, columnId }): void {
    // Clone from all columns and replace from updated tasks to outdated tasks in this column
    const column = getters.getColumn(state)(columnId);
    if (column) {
      column.tasks = tasks;
    }

    // UPDATE FROM STORAGE
    storage.set();
  },

  [MutationTypes.ADD_TASK](state, { task, columnId }): void {
    // Create Task data body
    const uid = getUID("task");
    const taskData: TaskData = {
      id: uid,
      ...task,
      comments: [],
    };

    // Find target column to add a task
    const column = getters.getColumn(state)(columnId);

    // Push task to column
    column?.tasks?.push(taskData);

    // UPDATE FROM STORAGE
    storage.set();
  },

  [MutationTypes.ADD_TASK](state, { task, columnId }): void {
    // Create Task data body
    const uid = getUID("task");
    const taskData: TaskData = {
      id: uid,
      ...task,
      comments: [],
      isArchive: state.isArchive,
    };

    // Find target column to add a task
    const column = getters.getColumn(state)(columnId);

    // Push task to column
    column?.tasks?.push(taskData);

    // UPDATE FROM STORAGE
    storage.set();
  },

  [MutationTypes.EDIT_TASK](state, { task: taskEdited, columnId }): void {
    // Find column index
    const COLUMN_INDEX = getters.getColumnIndex(state)(columnId);
    if (COLUMN_INDEX === undefined) return;
    // Find task index
    const TASK_INDEX = getters.getTaskIndex()({
      column: state.columns[COLUMN_INDEX],
      taskId: taskEdited.id,
    });
    if (TASK_INDEX === undefined) return;
    // Edit task to column
    state.columns[COLUMN_INDEX].tasks[TASK_INDEX] = taskEdited;

    // UPDATE FROM STORAGE
    storage.set();
  },

  [MutationTypes.DELETE_TASK](state, { task: taskDeleted, columnId }): void {
    // Find column index
    const COLUMN_INDEX = getters.getColumnIndex(state)(columnId);
    if (COLUMN_INDEX === undefined) return;
    // Delete task to column
    state.columns[COLUMN_INDEX].tasks = state.columns[
      COLUMN_INDEX
    ].tasks.filter((task) => task.id !== taskDeleted.id);

    // UPDATE FROM STORAGE
    storage.set();
  },

  [MutationTypes.ARCHIVE_TASK](state, { taskId, columnId, status }): void {
    // Find column index
    const COLUMN_INDEX = getters.getColumnIndex(state)(columnId);
    if (COLUMN_INDEX === undefined) return;
    // Find task index
    const taskIndex = getters.getTaskIndex()({
      column: state.columns[COLUMN_INDEX],
      taskId: taskId,
    });
    if (taskIndex === undefined) return;

    state.columns[COLUMN_INDEX].tasks[taskIndex].isArchive = status;

    // UPDATE FROM STORAGE
    storage.set();
  },

  [MutationTypes.ADD_COMMENT](state, { taskId, columnId, content }): void {
    // Create Comment Body
    const uid = getUID("comment");
    const commentData: Comment = {
      content,
      id: uid,
      date: getTodayDate(),
    };

    // Find column index
    const COLUMN_INDEX = getters.getColumnIndex(state)(columnId);
    if (COLUMN_INDEX === undefined) return;
    // Find Task index
    const taskIndex = getters.getTaskIndex()({
      column: state.columns[COLUMN_INDEX],
      taskId: taskId,
    });
    if (taskIndex === undefined) return;

    // push/unshift comment to "column/tasks/[comments.svg]" list
    state.columns[COLUMN_INDEX].tasks[taskIndex].comments.unshift(commentData);

    // UPDATE FROM STORAGE
    storage.set();
  },

  [MutationTypes.EDIT_COMMENT](
    state,
    { taskId, columnId, content, commentId }
  ): void {
    // Find column index
    const COLUMN_INDEX = getters.getColumnIndex(state)(columnId);
    if (COLUMN_INDEX === undefined) return;
    // Find Task index
    const taskIndex = getters.getTaskIndex()({
      column: state.columns[COLUMN_INDEX],
      taskId: taskId,
    });
    if (taskIndex === undefined) return;
    // Find comment index
    const commentIndex = getters.getCommentIndex()({
      task: state.columns[COLUMN_INDEX].tasks[taskIndex],
      commentId,
    });
    if (commentIndex === undefined) return;

    // change content of comment from "column/tasks/comments.svg/[COMMENT_INDEX]"
    state.columns[COLUMN_INDEX].tasks[taskIndex].comments[
      commentIndex
    ].content = content;

    // UPDATE FROM STORAGE
    storage.set();
  },

  [MutationTypes.DELETE_COMMENT](state, { taskId, columnId, commentId }): void {
    const COLUMN_INDEX = getters.getColumnIndex(state)(columnId);
    if (COLUMN_INDEX === undefined) return;
    // Find Task index
    const TASK_INDEX = getters.getTaskIndex()({
      column: state.columns[COLUMN_INDEX],
      taskId: taskId,
    });
    if (TASK_INDEX === undefined) return;

    // Delete comment from "column/tasks/comments.svg/[COMMENT_INDEX]"
    state.columns[COLUMN_INDEX].tasks[TASK_INDEX].comments = state.columns[
      COLUMN_INDEX
    ].tasks[TASK_INDEX].comments.filter((comment) => comment.id !== commentId);

    // UPDATE FROM STORAGE
    storage.set();
  },

  /*
   * ==================== SEARCH & FILTER ====================
   */
  [MutationTypes.SEARCH_COLUMNS](state, text): void {
    state.searchText = text;
  },
};

const storage = new Storage();
